// SPDX-License-Identifier: LicenseRef-VicZhuProprietary
// SPDX-FileCopyrightText: 2026 Vic Zhu <viczhu2014@gmail.com>

import { deadlineFetch } from '../hooks/customEndpointFetch'
import type { TreatmentPlan } from '../types'
import { authHeaders } from '../utils/api'

/**
 * Treatment Plan & Home Instructions (vic/vetpo#14, ada-core vic/ada-core#192).
 *
 * These do NOT go through the dataProvider's `treatment-plans` resource for
 * writes: the backend's single "Save & Send to Owner App" action is a custom
 * upsert-by-pet endpoint (POST /pet/:petId/save), not a plain PATCH — it is
 * the ONE entry point the backend allows so insulin plan + meal plan +
 * exceptions + reminder toggle + home instructions always land together in
 * one row. Reads still use the standard `treatment-plans` resource via
 * react-admin's dataProvider (see TreatmentPlanPanel.tsx), same as `emrs`.
 */

export interface InsulinProductOption {
  value: string
  label: string
  concentration: string
  syringeType: string
}

export interface TreatmentPlanSavePayload {
  insulinProduct?: string | null
  insulinDoseUnits?: number | null
  insulinTimes?: { time: string }[]
  mealTimes?: { time: string }[]
  eatingLessInstructions?: string | null
  notEatingInstructions?: string | null
  mealReminderEnabled?: boolean
  homeInstructions?: string | null
}

export type TreatmentPlanSaveResult =
  | { ok: true; plan: TreatmentPlan }
  | { ok: false; status: number; error: string }

export async function fetchInsulinProducts(): Promise<InsulinProductOption[]> {
  const response = await deadlineFetch('/api/treatment-plans/insulin-products', {
    headers: authHeaders(),
    credentials: 'include',
  })
  if (!response.ok) return []
  const body = (await response.json().catch(() => ({}))) as { products?: InsulinProductOption[] }
  return Array.isArray(body.products) ? body.products : []
}

export async function saveTreatmentPlan(
  petId: number | string,
  payload: TreatmentPlanSavePayload,
): Promise<TreatmentPlanSaveResult> {
  let response: Response
  try {
    response = await deadlineFetch(`/api/treatment-plans/pet/${petId}/save`, {
      method: 'POST',
      headers: authHeaders(),
      credentials: 'include',
      body: JSON.stringify(payload),
    })
  } catch (_e) {
    return { ok: false, status: 0, error: 'network' }
  }

  const body = (await response.json().catch(() => ({}))) as {
    success?: boolean
    plan?: TreatmentPlan
    errors?: { message?: string }[]
  }
  if (response.ok && body.success && body.plan) {
    return { ok: true, plan: body.plan }
  }
  return {
    ok: false,
    status: response.status,
    error: String(body.errors?.[0]?.message ?? 'unknown'),
  }
}
